import { useState, useEffect } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Calendar, Clock, User, AlertTriangle, CheckCircle } from 'lucide-react';
import { schedulingApi } from '@/lib/api/services/scheduling';
import { ApiError } from '@/lib/api';

interface Booking {
  id: string;
  invitee_name: string;
  invitee_email: string;
  start_time: string;
  end_time: string;
  timezone: string;
  status: string;
  payment_status?: string | null;
  cancellation_reason?: string;
  cancelled_by?: string;
  event_type: {
    name: string;
    duration: number;
    slug?: string;
  };
}

export default function CancelBooking() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const goBackToBookingPage = () => {
    const slug = booking?.event_type?.slug;
    if (slug) {
      navigate(`/book/${slug}`);
    } else {
      navigate('/');
    }
  };

  const fetchBooking = async () => {
    try {
      const response = await schedulingApi.getBookingById(bookingId!);
      if (response.success && response.data) {
        setBooking(response.data);
      } else {
        throw ApiError.fromResult(response, 'Booking not found');
      }
    } catch (err) {
      setError(friendlyMessage(err, "We couldn't load the booking. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!booking) return;
    
    setCancelling(true);
    try {
      const response = await schedulingApi.cancelBooking(
        bookingId!,
        cancellationReason.trim() || undefined,
        { actor: 'invitee' }
      );

      if (response.success) {
        setCancelled(true);
      } else {
        throw ApiError.fromResult(response, 'Failed to cancel booking');
      }
    } catch (err) {
      setError(friendlyMessage(err, "We couldn't cancel the booking. Please try again."));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading booking...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Booking Cancelled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Your booking has been successfully cancelled. Confirmation emails have been sent to all participants.
            </p>
            <Button onClick={goBackToBookingPage} className="w-full">
              Schedule Another
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Booking Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">The booking you're looking for could not be found.</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (booking.payment_status === 'paid' && booking.status !== 'cancelled') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Paid registration
              </CardTitle>
              <CardDescription>
                "{booking.event_type?.name || 'This event'}" — registration fees are non-refundable once paid.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  This registration can't be cancelled online because the payment has
                  already been processed. If you can't attend, please contact the host
                  (reply to your confirmation email) — they can arrange a refund or a
                  transfer manually where applicable.
                </AlertDescription>
              </Alert>
              <div className="flex justify-end">
                <Button variant="outline" onClick={goBackToBookingPage}>
                  Back to event page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (booking.status === 'cancelled') {
    const startDate = new Date(booking.start_time);
    const endDate = new Date(booking.end_time);

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Booking Cancelled
              </CardTitle>
              <CardDescription>
                This booking has already been cancelled.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-lg">{booking?.event_type?.name || 'Loading...'}</h3>
                
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{booking?.invitee_name || 'Loading...'}</span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>{startDate.toLocaleDateString('en-US', { timeZone: booking.timezone || 'UTC', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>
                    {startDate.toLocaleTimeString('en-US', { timeZone: booking.timezone || 'UTC', hour: 'numeric', minute: '2-digit', hour12: true })} - {endDate.toLocaleTimeString('en-US', { timeZone: booking.timezone || 'UTC', hour: 'numeric', minute: '2-digit', hour12: true })} ({booking?.timezone || 'UTC'})
                  </span>
                </div>
              </div>

              {booking.cancellation_reason && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Cancellation Details
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-red-700">Cancelled by: </span>
                      <span className="text-sm text-red-700">
                        {booking.cancelled_by === 'host' ? 'Host' : booking.invitee_name || 'Invitee'}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-red-700">Reason: </span>
                      <span className="text-sm text-red-700">{booking.cancellation_reason}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4">
                <Button onClick={goBackToBookingPage} className="w-full">
                  Schedule Another
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const startDate = new Date(booking.start_time);
  const endDate = new Date(booking.end_time);
  const bookingTz = booking.timezone || 'UTC';
  const formatInBookingTz = (date: Date, fmt: 'date' | 'time') => {
    if (fmt === 'date') {
      return date.toLocaleDateString('en-US', {
        timeZone: bookingTz,
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return date.toLocaleTimeString('en-US', {
      timeZone: bookingTz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancel Booking
            </CardTitle>
            <CardDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-semibold text-lg">{booking?.event_type?.name || 'Loading...'}</h3>
              
              <div className="flex items-center gap-2 text-gray-600">
                <User className="h-4 w-4" />
                <span>{booking?.invitee_name || 'Loading...'}</span>
              </div>
              
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>{formatInBookingTz(startDate, 'date')}</span>
              </div>

              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="h-4 w-4" />
                <span>
                  {formatInBookingTz(startDate, 'time')} - {formatInBookingTz(endDate, 'time')} ({booking?.timezone || 'UTC'})
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason for cancellation (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Please let us know why you're cancelling..."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                rows={3}
              />
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Cancelling this booking will send notification emails to all participants and cannot be undone.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={goBackToBookingPage}
                className="flex-1"
              >
                Keep Booking
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Booking'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}